import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AppImages } from "app/appImages";
import type { RootState } from 'app/store';
import { KnowledgeSubscriptionType } from "generated/CoreApi_v1";
import { LateralMenuItem } from "layouts/common/MenuCommon";
import { coreApiV1 } from "providers/CoreApiProvider";
import { KnowledgePaths } from "routes/KnowledgeRouter";

export type QuickAccessDto = {
    loading: boolean;
    items: LateralMenuItem[];
};

// Estado que será utilizado pelo REDUCER
interface MenuState {
    error: ApiProblemErrors | null;
    quickAccess: QuickAccessDto;
}

// Estado Inicial
const initialState: MenuState = {
    error: null,
    quickAccess: { loading: false, items: [] }
};

// Cria Reducer's através de SLICE.
const menuSlice = createSlice({
    name: 'notifications',
    initialState,
    reducers: {
        resetQuickAccess: (state) => state = {
            ...state, quickAccess: { loading: false, items: [] }
        }
    },
    extraReducers: (builder) => {
        ///
        builder.addCase(fetchKnowledgeQuickAccess.pending, (state) => {
            state.quickAccess = { loading: true, items: [] };
            state.error = null;
        });
        builder.addCase(fetchKnowledgeQuickAccess.fulfilled,
            (state, { payload }) => {
                state.quickAccess = { loading: false, items: payload };
            });
        builder.addCase(fetchKnowledgeQuickAccess.rejected,
            (state, { payload }) => {
                if (payload) state.error = payload;
                state.quickAccess = { loading: false, items: [] };
            });
    }
});

export const { resetQuickAccess } = menuSlice.actions;
export default menuSlice.reducer;

//#region Selectors

export const knowledgeQuickAccess = (state: RootState) => state.menu.quickAccess;

export const lmsQuickAccess = (state: RootState) => state.menu.quickAccess;

//#endregion


//#region Async Thunk

export const fetchKnowledgeQuickAccess =
    createAsyncThunk<LateralMenuItem[], {}, { rejectValue: ApiProblemErrors; }>
        ("menu/knowledgeQuickAccess", async (p, thunkApi) => {
            try {
                const result = await coreApiV1.knowledgeSubcription.getQuickAccessList({ onlyVisible: true });
                const blocks = result.data.map((item, index) => {
                    const path = item.subscriptionType == KnowledgeSubscriptionType.Channel
                        ? KnowledgePaths.FeedChannelHome(item.subscriptionId)
                        : item.subscriptionType == KnowledgeSubscriptionType.Topic
                            ? KnowledgePaths.FeedTopicHome(item.subscriptionId)
                            : item.subscriptionType == KnowledgeSubscriptionType.List
                                ? KnowledgePaths.FeedListHome(item.subscriptionId)
                                : item.subscriptionType == KnowledgeSubscriptionType.Author
                                    ? KnowledgePaths.FeedUserProfileHome(item.subscriptionId)
                                    : item.subscriptionType == KnowledgeSubscriptionType.Question
                                        ? KnowledgePaths.QuestionViewer(item.subscriptionId)
                                        : null;

                    const defaultThumbnail = item.subscriptionType == KnowledgeSubscriptionType.Channel
                        ? AppImages.KnowledgeChannelThumbnail
                        : item.subscriptionType == KnowledgeSubscriptionType.Topic
                            ? AppImages.TopicThumbnail
                            : item.subscriptionType == KnowledgeSubscriptionType.Author
                                ? AppImages.UserThumbnail
                                : item.subscriptionType == KnowledgeSubscriptionType.List
                                    ? AppImages.PublicationTypeListThumbnail
                                    : item.subscriptionType == KnowledgeSubscriptionType.Question
                                        ? AppImages.PublicationTypeQuestionThumbnail
                                        : null;

                    return {
                        name: item.name,
                        path: path,
                        thumbnailUrl: item.thumbnailUrl ?? defaultThumbnail,
                        matchCalloutId: [item.subscriptionId]
                    } as LateralMenuItem;
                });

                return blocks;
            }
            catch (e) {
                return thunkApi.rejectWithValue(e as ApiProblemErrors);
            }
        });


//#endregion
